import { FC, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { CustomCircularProgress } from 'Components/StyledComponents';
import { useNavigate } from 'react-router-dom';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import { useSelector } from 'react-redux';
import { MainAppReducer } from 'Types';
import { SIGN_IN_COMPONENT } from 'Types/AuthTypes';
import Logo from 'Components/Logo';
import IntroFlowBack from 'Components/IntroFlowBack';
import { AuthCardTheme } from 'Components/Forms/AuthPageParts';
import { useKeepFocusedFieldAboveKeyboard } from 'Components/Forms/useKeepFocusedFieldAboveKeyboard';
import SignIn from './SignIn';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import VerificationCode from './VerificationCode';

const Signin: FC<{}> = () => {
  const _navigate = useNavigate();
  const { userLevelData, userData } = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const {
    processPending,
    pendingEmailOrPhone: _pendingEmailOrPhone,
    signInComponent,
    resetEmailOrPhone: _resetEmailOrPhone,
    setSignInComponent,
    setPendingEmailOrPhone,
    resetErrors,
    user,
    // jwtToken,
    isAuthenticated,
  } = useAuthWrapperContext();
  const pageRef = useRef<HTMLDivElement>(null);
  const keyboardLiftStyle = useKeepFocusedFieldAboveKeyboard(pageRef);

  useEffect(() => {
    // this really shouldn't be necessary given default state but it isn't working for some reason
    if (setSignInComponent) setSignInComponent(SIGN_IN_COMPONENT.SIGNIN);
  }, [setSignInComponent]);

  const _navPlayGuest = () => {
    _navigate(`/welcome`);
  };

  const setComponentForgotPassword = () => {
    if (resetErrors) resetErrors();
    if (setSignInComponent) setSignInComponent(SIGN_IN_COMPONENT.FORGOT_PASSWORD);
  };

  const setComponentResetPassword = () => {
    if (resetErrors) resetErrors();
    if (setSignInComponent) setSignInComponent(SIGN_IN_COMPONENT.RESET_PASSWORD);
  };

  const setComponentSignIn = () => {
    if (resetErrors) resetErrors();
    if (setPendingEmailOrPhone) setPendingEmailOrPhone(undefined);
    if (setSignInComponent) setSignInComponent(SIGN_IN_COMPONENT.SIGNIN);
  };

  // useEffect(()=>{
  //   if(isPhoneNumber(resetEmailOrPhone)){
  //     dispatch(authActions.resetErrors())
  //     dispatch(authActions.setSignInComponent(SIGN_IN_COMPONENT.SIGNIN))
  //   }
  // }, [pendingEmailOrPhone])

  // useEffect(() => {
  //   if (!ready) return;           // AuthContext hasn’t finished initialising
  //   if (tokens) navigate('/roadmap');
  // }, [ready, tokens, navigate]);

  useEffect(() => {
    if (isAuthenticated && user) {
      if (setPendingEmailOrPhone) setPendingEmailOrPhone(undefined);
      // Ensure that the userData has BECOME the user data of the user that just signed in!!
      // Otherwise it will show the state according to user data values of the last user before the state switches over.
      // if (userData && userData.user_id == user?.attributes?.sub) { // ensure startup sagas has grabbed user data (or created inital state row)
      //   if (userData.subscription_status == "") { // Blank is before any trial has begun (new user state)
      //     navigate('/welcome')
      //   } else {
      //     navigate('/roadmap')
      //   }
      // }
    }
  }, [isAuthenticated, user, userLevelData, userData, setPendingEmailOrPhone]);

  return (
    <Box className="page-div-styles" ref={pageRef} style={keyboardLiftStyle}>
      <Box className="filter-div-styles">
        <Box className="sign-in-div-styles">
          {signInComponent === SIGN_IN_COMPONENT.FORGOT_PASSWORD && (
            <IntroFlowBack handleBack={setComponentSignIn} />
          )}
          <Logo />
          <Box className="desc-container welcome-container">
            {processPending && (
              <Box
                sx={{
                  display: 'flex',
                  position: 'absolute',
                  height: '100%',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CustomCircularProgress />
              </Box>
            )}

            {signInComponent === SIGN_IN_COMPONENT.SIGNIN && (
              <SignIn
                forgotPasswordNavFunction={setComponentForgotPassword}
                goBackFn={setComponentSignIn}
              />
            )}

            {signInComponent === SIGN_IN_COMPONENT.FORGOT_PASSWORD && (
              <ForgotPassword goToReset={setComponentResetPassword} />
            )}

            {signInComponent === SIGN_IN_COMPONENT.RESET_PASSWORD && (
              <ResetPassword
                goToSignIn={setComponentSignIn}
                goBackFn={setComponentForgotPassword}
              />
            )}

            {signInComponent === SIGN_IN_COMPONENT.VERIFICATION_CODE && (
              <VerificationCode backFn={setComponentSignIn} />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const _backgroundStyles1 = {
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  height: '100vh',
};

const _backgroundStyles2 = {
  width: '100%',
  height: '100%',
  backdropFilter: 'brightness(15%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
};

const SigninPage: FC<{}> = () => (
  <AuthCardTheme>
    <Signin />
  </AuthCardTheme>
);

export default SigninPage;
